/* ============
 * Add multiple products modal
 * ============
 *
 * This is the component for creating batch log
 */
import { mapGetters, mapMutations } from 'vuex';
import unitTypeService from './../../services/unitType';
import batchService from './../../services/batch';
import batchTransformer from './../../transformers/batch';
import store from './../../store';

export default {
  data() {
    return {
      search: '',
      selected: [],
      loading: false,
      submitLoading: false,
      allProducts: [],
      batchOptions: [],
    };
  },
  props: {
    isOrderPage: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableCustomBatch: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableQuantity: {
      type: Boolean,
      required: false,
      default: false,
    },
    batches: {
      type: Array,
      required: false,
      default: () => [],
    },
    warehouseId: {
      type: Number,
      required: false,
      default: 0,
    },
    warehouseIdForBatch: {
      type: Number,
      required: false,
      default: 0,
    },
    fetchProductsMethod: {
      type: Function,
      required: true,
      default: () => {},
    },
    onSave: {
      type: Function,
      required: true,
      default: () => {},
    },
    selectedProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  created() {
    if (this.batches.length) {
      this.batchOptions = [...this.batches];
    }
    if (this.selectedProducts.length) {
      this.selected = [...this.selectedProducts];
    }
  },
  computed: {
    ...mapGetters({
      settings: 'organizationSettings',
      unitTypes: 'allUnitTypes',
      account: 'account',
    }),
    products() {
      const selectedIds = this.selected ? this.selected.map(s => s.id) : [];
      return this.allProductOptions.filter(p => !selectedIds.includes(p.id));
    },
    allProductOptions() {
      return this.allProducts.map((p) => {
        const selectWarehouseQty = p.warehouses.find(p2 => p2.warehouseId === this.warehouseId);
        let currentQty = selectWarehouseQty ? selectWarehouseQty.qtyNumber : 0;

        let isDisabled = false;
        if (this.warehouseId !== 0) {
          isDisabled = !(!p.stockable || p.warehouses.find(pw => +pw.warehouseId === +this.warehouseId));
        }

        if (p.productType.name === 'Bundle') {
          isDisabled = false;
          currentQty = 1;
        }

        if (this.batchOptions) {
          const batches = this.getProductBatches(p);
          if (batches.length) {
            currentQty = batches
              .map(item => item.qtyNumber || 0)
              .reduce((total, qtyNumber) => total + qtyNumber);
          }
          
          let defaultBatchId = '';
          if (batches.length === 1) defaultBatchId = batches[0].id
          else if (batches.length > 1) defaultBatchId = batches[1].id
    
          const batchesWithStocks = batches.filter(b => b.qtyNumber > 0);
          if (batchesWithStocks.length === 1) {
            defaultBatchId = batchesWithStocks[0].id;
          }
          return Object.assign({}, p, {
            qty: 1,
            currentQty,
            productType: p.productType.name,
            info: ('info' in p) ? p.info : this.getProductInfo(p),
            batchId: defaultBatchId,
            batches,
            disabled: isDisabled,
            productTypeGroup: p.productTypeGroup,
            unitQty: p.productTypeGroup === 'Beverage' ? 1 : '',
            unitTypeId: p.productTypeGroup === 'Beverage' ? 1 : '',
          });
        }
        return Object.assign({}, p, {
          qty: 1,
          currentQty,
          productType: p.productType.name,
          info: ('info' in p) ? p.info : this.getProductInfo(p),
          disabled: isDisabled,
          productTypeGroup: p.productTypeGroup,
          unitQty: p.productTypeGroup === 'Beverage' ? 1 : '',
          unitTypeId: p.productTypeGroup === 'Beverage' ? 1 : '',
        });
      });
    },
  },

  mounted() {
    this.loading = true;
    Promise.all([
      unitTypeService.all(),
    ]).then(() => {
      this.ready = true;
    });
    this.fetchProducts();
  },
  methods: {
    ...mapMutations({
      toggle: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
    }),

    fetchProducts() {
      this.fetchProductsMethod().then((data) => {
        this.allProducts = data[0];
        if (this.selected.length) {
          const selectedIds = this.selected.map(s => +s.id);
          this.selected = this.allProductOptions.filter(p => selectedIds.includes(+p.id));
        }
        this.loading = false;
      });
    },

    getProductInfo(item) {
      const tags = this.$i18n.messages[this.$i18n.locale].form.products;
      const details = [];
      this.settings.productInfoDisplay.forEach((field) => {
        if (item[field] !== undefined) {
          details.push(`${tags[field].label}: ${item[field]}`);
        } else if (item.fieldsJSON[field] !== undefined) {
          details.push(`${tags[field].label}: ${item.fieldsJSON[field]}`);
        }
      });
      return details.join(' | ');
    },
    getProductBatches(item) {
      let productWarehouses = item.warehouses;
      if (this.warehouseId) {
        productWarehouses = productWarehouses.filter(p => +p.warehouseId === +this.warehouseId);
      }
      const batchIds = productWarehouses.map(p => +p.batchId);
      let batches = this.batchOptions.filter(b => batchIds.includes(+b.id)).map((batch) => {
        const batchProduct = productWarehouses.find(p => +p.batchId === +batch.id);
        return Object.assign({}, {
          id: batch.id,
          name: this.warehouseId ? `${batch.code} | ${this.$t('static.orders.edit.availableQty')}: ${batchProduct.qty}` : batch.code,
          qtyNumber: batchProduct.qtyNumber,
        });
      });
      batches = batches.reverse();

      if (this.$route.name === 'orders.edit') {
        batches = batches.filter(batch => batch.qtyNumber > 0);
        batches.sort((a, b) => b.id - a.id);
      }

      const nonBatchProduct = productWarehouses.find(p => p.batchId === 0);
      if (nonBatchProduct) {
        batches.unshift({
          id: '0',
          name: `${this.$t('static.orders.edit.nonBatch')} | ${this.$t('static.orders.edit.availableQty')}: ${nonBatchProduct.qty}`,
          qtyNumber: nonBatchProduct.qtyNumber,
        });
      }

      return batches;
    },

    handleCurrentChange(selected) {
      if (!selected || selected.disabled) return;
      this.selected.push(selected);
    },

    handleRemove(index) {
      this.selected.splice(index, 1);
    },

    handleSubmit() {
      this.submitLoading = true;
      this.onSave(this.selected).then(() => {
        this.submitLoading = false;
        this.closeModal();
      }).catch((errors) => {
        this.submitLoading = false;
        if (errors && errors.items) {
          this.$message({
            showClose: true,
            message: this.$t('static.bulkProductAdd.emptySelectionError'),
            type: 'error',
            customClass: 'bulk-add-product-modal-message',
          });
          return;
        }
        const msg = this.extractBulkSaveErrorMessage(errors);
        if (msg) {
          this.$message({
            showClose: true,
            message: msg,
            type: 'error',
            customClass: 'bulk-add-product-modal-message',
          });
        }
      });
    },

    /**
     * Normalize API error payloads (Laravel validation, credit note cap, etc.) for display above the dialog.
     *
     * @param {object|string|undefined} errors
     * @returns {string|null}
     */
    extractBulkSaveErrorMessage(errors) {
      if (!errors) {
        return null;
      }
      if (typeof errors === 'string') {
        return errors;
      }
      if (typeof errors !== 'object') {
        return null;
      }
      if (errors.exceed_creditnote_amount && errors.exceed_creditnote_amount[0]) {
        return errors.exceed_creditnote_amount[0];
      }
      if (errors.exceedCreditnoteAmount && errors.exceedCreditnoteAmount[0]) {
        return errors.exceedCreditnoteAmount[0];
      }
      if (errors.errors && typeof errors.errors === 'object') {
        const firstKey = Object.keys(errors.errors)[0];
        const arr = firstKey ? errors.errors[firstKey] : null;
        if (Array.isArray(arr) && arr[0]) {
          return arr[0];
        }
      }
      if (errors.message && typeof errors.message === 'string') {
        return errors.message;
      }
      const keys = Object.keys(errors);
      for (let i = 0; i < keys.length; i += 1) {
        const val = errors[keys[i]];
        if (Array.isArray(val) && val[0] && typeof val[0] === 'string') {
          return val[0];
        }
      }
      return this.$t('static.bulkProductAdd.saveError');
    },

    searchTableRowClassName({ row }) {
      if (row.disabled) {
        return 'disabled';
      }
    },

    /**
     * Disable submit button in case of loading and non selected products.
     */
    disableSubmit() {
      return Boolean(!this.loading && !this.selected.length > 0);
    },

    /*
     * Closes the modal
     */
    closeModal() {
      this.toggle(false);
      this.unsetModalContent();
    },

    addBatch(product) {
      this.$prompt(`${this.$t('form.batches.batchNumber.label')}`, this.$t('static.batches.create.title'), {
        confirmButtonText: this.$t('button.save'),
        cancelButtonText: this.$t('button.cancel'),
      }).then(({
        value,
      }) => {
        this.batchLoading = true;
        const batch = batchTransformer.send({
          batchNumber: value,
          status: 'completed',
          custom: 1,
          productId: product.id,
          warehouseId: this.warehouseIdForBatch,
        });
        batchService.store(batch)
        .then((data) => {
          store.dispatch('addBatch', data);
          this.batchOptions.push(data);
          this.fetchProducts();
          this.batchLoading = false;
          this.$message({
            message: this.$t('static.batches.create.success'),
            type: 'success',
          });
        }).catch(() => {
          this.batchLoading = false;
          this.$message({
            message: this.$t('static.batches.create.error'),
            type: 'error',
          });
        });
      });
    },

  },
  components: {
    VForm: require('./../../components/form/form.vue'),
    VMessage: require('./../../components/form/message/message.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/bulkAddProductModal/bulkAddProductModal.js